import React from "react";
import GeneralTab from "./Tabs/GeneralTab";
import AccordionGroup from "../../../../../../components/AccordionGroup";
import {
  Notes,
  definitions,
  manufacturingProcedures,
  ndeProcedures,
  optionsByType,
} from "./utils";
import ITPRowsTab from "./Tabs/ITPRowsTab";
import { SelectRev } from "./Tabs/Components/SelectRev";
import { Box, Typography } from "@mui/material";
import { useGetItpsByProjectAndType } from "./hooks";
import PlantTasksWrapper from "../../../../Project/plant/PlantTaskPage/PlantTasksWrapper";
import DynamicITPFrontOnly from "./Tabs/Components/DynamicITPColumns";
import { AuthContext } from "../../../../../../context/authContext";
import { getPlantData } from "../../PMSTab2/4d/api";

const revs = ["Rev-00", "Rev-01"];

const ITP_TYPES = [
  "Documents Review and Approval",
  "Steel Structure Fabrication (Separate ITP will be provided if sub-contracted)",
  "ASME Pressure Equipment Fabrication (Separate ITP will be provided if sub-contracted)",
  "Piping Fabrication",
  "Assembly and Testing",
  "Preparation for Shipment",
];

const itpKeys = [
  "GENERAL",
  "CUMULATIVE PROGRESS",
  "ITP TABLES",
  "NDE PROCEDURES",
  "MANUFACTURING PROCEDURES",
  "PROJECT SPECIFICATION",
  "INTERVENTION POINT",
];

const getActualPlantId = (plantId) => {
  if (!plantId) return "";

  if (typeof plantId === "object") {
    return plantId?.id || plantId?._id || plantId?.plantId || "";
  }

  return plantId;
};

export default function ITPTable({
  plant = false,
  plantId,
  projectFilter = [],
}) {
  const { accessToken } = React.useContext(AuthContext);

  const [rev, setRev] = React.useState(revs[0]);
  const [selectedType, setSelectedType] = React.useState(ITP_TYPES[0]);
  const [allTasks, setAllTasks] = React.useState([]);
  const [allTasksLoading, setAllTasksLoading] = React.useState(false);

  const actualPlantId = React.useMemo(() => {
    return getActualPlantId(plantId);
  }, [plantId]);

  const loadAllTasks = React.useCallback(async () => {
    if (!actualPlantId) {
      setAllTasks([]);
      return;
    }

    try {
      setAllTasksLoading(true);

      const tasks = await getPlantData(actualPlantId, accessToken);
      const tasksArray = Array.isArray(tasks) ? tasks : [];
      const itpTasksOnly = tasksArray.filter(
        (task) => String(task?.type || "").trim().toLowerCase() === "itp",
      );

      console.log("[ITP] 4D ITP tasks", {
        plantId: actualPlantId,
        count: itpTasksOnly.length,
        tasks: itpTasksOnly,
      });

      setAllTasks(itpTasksOnly);
    } catch (error) {
      console.error("[ITP] Failed to load 4D tasks:", error);
      setAllTasks([]);
    } finally {
      setAllTasksLoading(false);
    }
  }, [actualPlantId, accessToken]);

  React.useEffect(() => {
    loadAllTasks();
  }, [loadAllTasks]);

  const handleRevChange = React.useCallback((event) => {
    setRev(event.target.value);
  }, []);

  const handleTypeChange = React.useCallback((event) => {
    setSelectedType(event.target.value);
  }, []);

  const useGetItemsByType = React.useMemo(() => {
    const bindType = (type) =>
      useGetItpsByProjectAndType.bind(null, type, plant, projectFilter);

    return {
      "PRE-FABRICATION": bindType("PRE-FABRICATION"),
      "MANUFACTURING & NDE": bindType("MANUFACTURING & NDE"),
      "PWHT & NDE POST PWHT": bindType("PWHT & NDE POST PWHT"),
      "HYDROSTATIC TEST": bindType("HYDROSTATIC TEST"),
      "FINAL PREPARATION": bindType("FINAL PREPARATION"),
    };
  }, [plant, projectFilter]);

  const useSelectedHook = useGetItemsByType[selectedType];

  const itpPanels = React.useMemo(
    () => [
      <GeneralTab generalData={{ rev }} />,

      <PlantTasksWrapper type="itp" isPCSTab={true} />,

      <DynamicITPFrontOnly
        ITP_TYPES={ITP_TYPES}
        optionsByType={optionsByType}
        allTasks={allTasks}
        allTasksLoading={allTasksLoading}
        refreshAllTasks={loadAllTasks}
      />,

      <ITPRowsTab rows={ndeProcedures} />,

      <ITPRowsTab rows={manufacturingProcedures} />,

      <ITPRowsTab rows={Notes} />,

      <ITPRowsTab rows={definitions} />,
    ],
    [
      rev,
      selectedType,
      handleTypeChange,
      useSelectedHook,
      allTasks,
      allTasksLoading,
      loadAllTasks,
    ],
  );

  return (
    <AccordionGroup
      keys={itpKeys}
      panels={itpPanels}
      keysAsComponents={{
        GENERAL: (
          <Box sx={{ m: 0 }}>
            <Box sx={{ display: "inline-block" }}>
              <Typography
                sx={{
                  fontWeight: "bold",
                  pr: 2,
                  transform: "translateY(8px)",
                }}
              >
                GENERAL
              </Typography>
            </Box>

            <SelectRev
              rev={rev}
              handleRevChange={handleRevChange}
              revs={revs}
            />
          </Box>
        ),
      }}
    />
  );
}